import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import FramedImage from "../../components/FramedImage/FramedImage";
import { getHiddenItems } from "../../utilities/items-service";
import { thumbUrl } from "../../utilities/image-urls";
import {
  itemPath,
  KIND_LABELS,
  plainDescription,
} from "../../utilities/item-kinds";

const PAGE_SIZE = 30;

export default function HiddenItemsPage({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    const params = new URLSearchParams();
    params.set("page", pageNum);
    params.set("limit", PAGE_SIZE);

    const response = await getHiddenItems(params);

    if (response && response.items) {
      setItems((prev) =>
        append ? [...prev, ...response.items] : response.items,
      );
      setHasMore(response.hasMore);
      setPage(pageNum);
    } else {
      console.log("error: ", response);
    }

    if (append) {
      setIsLoadingMore(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  // Intersection Observer for endless scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: "400px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, page, fetchPage]);

  if (!user?.isAdmin) {
    return (
      <div className="text-center py-20">
        <p className="text-ink/50 mb-4">Not authorised</p>
        <button
          className="text-sm underline text-ink"
          onClick={() => navigate("/")}
        >
          Return to home
        </button>
      </div>
    );
  }

  return (
    <section className="max-w-[1287px] mx-auto px-6 py-10">
      <h1 className="font-display text-2xl text-ink mb-2">Hidden items</h1>
      <p className="font-serif text-sm text-ink/60 mb-8">
        These items are excluded from the public archive.
      </p>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader />
        </div>
      ) : items.length === 0 ? (
        <p className="font-serif text-ink/50 py-10">No hidden items.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {items.map((item) => (
              <Link
                key={item._id}
                to={itemPath(item)}
                className="group bg-cream border border-ink/10 rounded-md overflow-hidden flex flex-col"
              >
                {/* Framed with the tile settings and the mosaic's exact
                    235:300 ratio, so this previews the true tile crop */}
                <div className="relative aspect-[235/300] flex items-center justify-center">
                  {item.image ? (
                    <FramedImage
                      src={item.image}
                      thumbSrc={thumbUrl(item.image)}
                      alt={item.subject || "Hidden item"}
                      framing={item.framing?.tile}
                      hoverZoom
                      loading="lazy"
                    />
                  ) : (
                    <p className="font-serif text-sm text-ink/60 line-clamp-4 px-6">
                      {item.subject || plainDescription(item.description)}
                    </p>
                  )}
                </div>
                <div className="px-3 pb-3 mt-auto">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-seal">
                    {KIND_LABELS[item.kind] || "Stamp"}
                  </p>
                  <p className="font-serif text-xs text-ink/70 truncate">
                    {item.subject || item.catalogue_id}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          {/* Sentinel element for infinite scroll */}
          <div ref={sentinelRef} className="h-1" />
          {isLoadingMore && (
            <div className="flex justify-center items-center py-10">
              <Loader />
            </div>
          )}
        </>
      )}
    </section>
  );
}
